import React, { useEffect, useState } from "react";
import { Message } from "ai/react";
import { Icon, Typography12, Typography13 } from "@slid/slid-ips";
import styled from "styled-components";
import { MediumNoteMateIcon } from "components/icons/NoteMateIcons/MediumNoteMateIcon";
import { SmallNoteMateIcon } from "components/icons/NoteMateIcons/SmallNoteMateIcon";
import { CopyIcon } from "components/icons/CopyIcon";
import { SLIDDY_CHAT_ROLE } from ".";
import loading_animation_blue from "components/animations/loading_animation_blue.json";
import Lottie from "react-lottie";
import { useTranslation } from "react-i18next";
import { eventTypes } from "types/eventTracking";
import { trackEvent } from "utils/eventTracking";
const defaultLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: loading_animation_blue,
  renderer: "svg",
};

export const ChatBox = ({
  messages,
  isThinking,
  isLoading,
  parentRef,
  chatError,
  isDarkTheme,
  isDesktopVideonote = false,
  inputHeight,
}: {
  messages: Message[];
  isThinking: boolean;
  isLoading: boolean;
  parentRef: React.RefObject<HTMLDivElement>;
  chatError: boolean | null;
  isDarkTheme: boolean;
  isDesktopVideonote?: boolean;
  inputHeight: number;
}) => {
  const AI_ROLE = SLIDDY_CHAT_ROLE.assistant;
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  useEffect(() => {
    if (copiedMessageId) {
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 3000);
    }
  }, [copiedMessageId]);

  useEffect(() => {
    if (chatError) {
      trackEvent({
        eventType: eventTypes.show.ERROR_IN_NOTE_MATE,
      });
    }
  }, [chatError]);

  const renderMessageText = (messageContent, messageRole, isBeingGenerated, messageIndex, messageCount) => {
    const isLastMessage = messageIndex === messageCount - 1;
    const shouldAppendLoadingDots = messageRole === AI_ROLE && isBeingGenerated && isLastMessage;
    const messageText = `${messageContent.trim()}${shouldAppendLoadingDots ? "..." : ""}`;
    return <MessageText text={messageText} color={isDarkTheme ? "--white" : "--gray16"} weight={400} />;
  };

  if (isDesktopVideonote) {
    return (
      <ChatBoxContainer ref={parentRef} inputHeight={inputHeight}>
        {messages.map((messageData, index: number) => (
          <MessageContainer key={index} isAiMessage={messageData.role === AI_ROLE} isDesktopVersion={isDesktopVideonote}>
            {messageData.role === AI_ROLE && <SmallNoteMateIcon />}
            <MessageWrapper isAiMessage={messageData.role === AI_ROLE} isThinking={false} isDarkTheme={isDarkTheme}>
              <MessageItem key={index}>{renderMessageText(messageData.content, messageData.role, isLoading, index, messages.length)}</MessageItem>
              {messageData.role === AI_ROLE && (
                <CopyComponent index={index} copiedMessageId={copiedMessageId} setCopiedMessageId={setCopiedMessageId} messageData={messageData} isDarkTheme={isDarkTheme} />
              )}
            </MessageWrapper>
            {index === messages.length - 1 && messageData.role === AI_ROLE && <ChatBoxFooter isDesktopVersion={isDesktopVideonote} />}
          </MessageContainer>
        ))}
        {isThinking && <ThinkingItem isDarkTheme={isDarkTheme} isDesktopVersion={isDesktopVideonote} />}
        {chatError && <ErrorMessageItem isDarkTheme={isDarkTheme} isDesktopVersion={isDesktopVideonote} />}
      </ChatBoxContainer>
    );
  }

  return (
    <ChatBoxContainer ref={parentRef} inputHeight={inputHeight}>
      {messages.map((messageData, index: number) => (
        <MessageContainer key={index} isAiMessage={messageData.role === AI_ROLE} isDesktopVersion={isDesktopVideonote}>
          {messageData.role === AI_ROLE && <MediumNoteMateIcon />}
          <MessageWrapper isAiMessage={messageData.role === AI_ROLE} isThinking={false} isDarkTheme={isDarkTheme}>
            <MessageItem key={index}>{renderMessageText(messageData.content, messageData.role, isLoading, index, messages.length)}</MessageItem>
            {messageData.role === AI_ROLE && (
              <CopyComponent index={index} copiedMessageId={copiedMessageId} setCopiedMessageId={setCopiedMessageId} messageData={messageData} isDarkTheme={isDarkTheme} />
            )}
          </MessageWrapper>
          {index === messages.length - 1 && messageData.role === AI_ROLE && <ChatBoxFooter isDesktopVersion={isDesktopVideonote} />}
        </MessageContainer>
      ))}
      {isThinking && <ThinkingItem isDarkTheme={isDarkTheme} isDesktopVersion={isDesktopVideonote} />}
      {chatError && <ErrorMessageItem isDarkTheme={isDarkTheme} isDesktopVersion={isDesktopVideonote} />}
    </ChatBoxContainer>
  );
};

const CopyComponent = ({
  index,
  copiedMessageId,
  setCopiedMessageId,
  messageData,
  isDarkTheme,
}: {
  index: number;
  copiedMessageId: number | null;
  setCopiedMessageId: (id: number) => void;
  messageData: Message;
  isDarkTheme: boolean;
}) => {
  const { t } = useTranslation("NoteMate");

  return (
    <>
      {copiedMessageId === index ? (
        <CopyContainer>
          <Icon icon="check24" width={16} height={16} color="--gray9" />
          <Typography12 weight={500} color="--gray9" text={t("ChatCopySuccessMessage")} />
        </CopyContainer>
      ) : (
        <CopyContainer
          onClick={() => {
            setCopiedMessageId(index);
            navigator.clipboard.writeText(messageData.content);
          }}
        >
          <CopyIcon width={16} height={16} />
          <Typography12 weight={500} color={isDarkTheme ? "--blue6" : "--blue8"} text={t("ChatCopyButton")} />
        </CopyContainer>
      )}
    </>
  );
};

const ThinkingItem = ({ isDarkTheme, isDesktopVersion }: { isDarkTheme: boolean; isDesktopVersion: boolean }) => {
  return (
    <MessageContainer isAiMessage={true} isDesktopVersion={isDesktopVersion}>
      {isDesktopVersion ? <SmallNoteMateIcon /> : <MediumNoteMateIcon />}
      <MessageWrapper isAiMessage={true} isThinking={true} isDarkTheme={isDarkTheme}>
        <LoadingContainer>
          <Lottie options={defaultLottieOptions} height={28} width={28} />
        </LoadingContainer>
      </MessageWrapper>
      <ChatBoxFooter isDesktopVersion={isDesktopVersion} />
    </MessageContainer>
  );
};

const ErrorMessageItem = ({ isDarkTheme, isDesktopVersion }: { isDarkTheme: boolean; isDesktopVersion: boolean }) => {
  const { t } = useTranslation("NoteMate");
  return (
    <MessageContainer isAiMessage={true} isDesktopVersion={isDesktopVersion}>
      {isDesktopVersion ? <SmallNoteMateIcon /> : <MediumNoteMateIcon />}
      <ErrorMessageWrapper isAiMessage={true} isThinking={false} isDarkTheme={isDarkTheme}>
        <Typography13 text={t("ChatErrorMessage")} color={`--white`} weight={400} />
      </ErrorMessageWrapper>
      <ChatBoxFooter isDesktopVersion={isDesktopVersion} />
    </MessageContainer>
  );
};

const ChatBoxContainer = styled.div<{ inputHeight: number }>`
  width: 100%;
  height: ${({ inputHeight }) => `calc(100% - 24px - ${inputHeight}px)`};
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 12px;

  // 스크롤 바 숨기기
  &::-webkit-scrollbar {
    width: 0; // 스크롤 바 너비를 0으로 설정
    background: transparent; // 배경을 투명으로 설정
  }
`;

const MessageContainer = styled.div<{ isAiMessage: boolean; isDesktopVersion: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: ${({ isAiMessage }) => (isAiMessage ? `flex-start` : `flex-end`)};
  gap: ${({ isDesktopVersion }) => (isDesktopVersion ? "2px" : "8px")};
`;

const MessageWrapper = styled.div<{ isAiMessage: boolean; isThinking: boolean; isDarkTheme: boolean }>`
  display: flex;
  width: ${(props) => (props.isThinking ? "100%" : "fit-content")};
  flex-direction: column;
  align-items: flex-start;

  padding: 12px 16px;
  background-color: ${({ isAiMessage, isDarkTheme }) => (isAiMessage ? (isDarkTheme ? "var(--gray17)" : "var(--gray2)") : isDarkTheme ? "var(--blue11)" : "var(--blue2)")};
  gap: 12px;
  border-radius: 12px;
`;

const MessageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopyContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 4px;
`;

const LoadingContainer = styled.div``;

const ErrorMessageWrapper = styled(MessageWrapper)<{ isDarkTheme: boolean }>`
  width: 100%;
  background-color: ${({ isDarkTheme }) => (isDarkTheme ? "var(--red1_opacity)" : "var(--red1)")};
`;

const ChatBoxFooter = styled.div<{ isDesktopVersion: boolean }>`
  width: 100%;
  height: ${({ isDesktopVersion }) => (isDesktopVersion ? "20px" : "32px")};
`;

const MessageText = styled(Typography13)`
  & {
    word-break: break-all;
  }
`;
